import { useState, useMemo } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { Search, Smartphone, Globe, Check, X } from "lucide-react";
import { useDeviceAppRequests, useDeviceWebRequests } from "@/hooks/useDeviceRequests";
import { format } from "date-fns";
import { DeviceSidebar } from "./DeviceSidebar";
import { ConvexErrorBoundary } from "./ConvexErrorBoundary";
import { toast } from "@/hooks/use-toast";
import { RequestAppDisplay } from "./RequestAppDisplay";
import { useMutation } from "convex/react";
import { api } from "../../api";


interface DeviceRequestsTabProps {
  deviceId: string;
}

const requestsSidebarItems = [
  { id: "websites", label: "Websites", icon: Globe },
  { id: "apps", label: "Apps", icon: Smartphone },
];

const DeviceRequestsContent = ({ deviceId }: DeviceRequestsTabProps) => {
  const [activeSection, setActiveSection] = useState<"apps" | "websites">("websites");
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState<string>("all");
  
  const { requests: appRequests, isLoading: isLoadingApps, error: appError } = useDeviceAppRequests(deviceId);
  const { requests: webRequests, isLoading: isLoadingWebs, error: webError } = useDeviceWebRequests(deviceId);
  const approveForDevice = useMutation(api.requests.respond.approveForDevice);
  const rejectForDevice = useMutation(api.requests.respond.rejectForDevice);

  const isLoading = activeSection === "apps" ? isLoadingApps : isLoadingWebs;
  const requests = activeSection === "apps" ? appRequests : webRequests;
  const error = activeSection === "apps" ? appError : webError;

  // Filter requests by search term and status
  const filteredRequests = useMemo(() => {
    return requests.filter(request => {
      const matchesSearch = searchTerm === "" || 
        (request.name && request.name.toLowerCase().includes(searchTerm.toLowerCase()));
      const matchesStatus = statusFilter === "all" || 
        (request.response && request.response === statusFilter);
      
      return matchesSearch && matchesStatus;
    });
  }, [requests, searchTerm, statusFilter]);

  const getStatusVariant = (status?: string) => {
    switch (status) {
      case 'level':
      case 'device':
        return 'default';
      case 'deny': 
        return 'destructive';
      default: 
        return 'outline';
    }
  };

  const getStatusClassName = (status?: string) => {
    switch (status) {
      case 'level':
      case 'device':
        return 'bg-green-100 text-green-800 border-green-200 hover:bg-green-100';
      default:
        return '';
    }
  };

  const getStatusLabel = (status?: string) => {
    if (!status) return 'Pending';
    switch (status) {
      case 'level': return 'Approved (Level)';
      case 'device': return 'Approved (Device)';
      case 'deny': return 'Denied';
      default: return status;
    }
  };

  const handleAcceptRequest = async (request: any, index: number) => {
    try {
      // Use app data directly from the request
      const appData = {
        name: request.name || "Unknown App",
        iconUrl: request.iconUrl || "",
        trackId: request.trackId || "",
        category: request.category || "",
        tags: request.tags || [],
        version: request.version,
      };
      
      await approveForDevice({
        request: request._id,
        app: appData,
      });
      
      toast({
        title: "Request Approved",
        description: `${appData.name} has been approved for this device.`,
      });
    } catch (error) {
      console.error("Error approving request:", error);
      toast({
        title: "Error",
        description: error instanceof Error ? error.message : "Failed to approve request",
        variant: "destructive",
      });
    }
  };

  const handleDeclineRequest = async (request: any, index: number) => {
    try {
      await rejectForDevice({
        request: request._id,
      });
      
      toast({
        title: "Request Declined",
        description: `${request.name} has been denied.`,
        variant: "destructive",
      });
    } catch (error) {
      console.error("Error declining request:", error);
      toast({
        title: "Error",
        description: error instanceof Error ? error.message : "Failed to decline request",
        variant: "destructive",
      });
    }
  };

  if (isLoading) {
    return (
      <div className="p-6 space-y-4">
        <div className="h-8 bg-muted rounded animate-pulse"></div>
        <div className="h-64 bg-muted rounded animate-pulse"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6">
        <Card>
          <CardContent className="pt-6">
            <div className="text-center py-12">
              <p className="text-destructive font-semibold mb-2">Error Loading Requests</p>
              <p className="text-sm text-muted-foreground">{error}</p>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="flex h-full">
      <DeviceSidebar
        title="Requests"
        items={requestsSidebarItems}
        activeSection={activeSection}
        onSectionChange={(id) => setActiveSection(id as "apps" | "websites")}
      />
      <div className="flex-1 overflow-hidden">
        <div className="h-full overflow-y-auto p-3 space-y-4">

      {/* Filters */}
      <Card>
        <CardContent className="pt-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder={activeSection === "apps" ? "Search by app name" : "Search by URL"}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-9"
              />
            </div>
            
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger>
                <SelectValue placeholder="Filter by status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Statuses</SelectItem>
                <SelectItem value="level">Approved (Level)</SelectItem>
                <SelectItem value="device">Approved (Device)</SelectItem>
                <SelectItem value="deny">Denied</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Requests Table */}
      <Card>
        <CardHeader>
          <CardTitle>Requests ({filteredRequests.length})</CardTitle>
          <CardDescription>
            {filteredRequests.length === 0 
              ? "No requests found for this device"
              : "Click on a request to view more details"}
          </CardDescription>
        </CardHeader>
        <CardContent>
          {filteredRequests.length === 0 ? (
            <div className="text-center py-12 text-muted-foreground">
              <p>No requests found matching your filters</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>{activeSection === "apps" ? "App Name" : "URL"}</TableHead>
                    <TableHead>Message</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredRequests.map((request, index) => (
                    <TableRow key={`${request.device}-${index}`} className="hover:bg-muted/50">
                      <TableCell>
                        {activeSection === "apps" ? (
                          <RequestAppDisplay 
                            bundleId={request.bundleId} 
                            appName={request.name}
                            iconUrl={request.iconUrl}
                          />
                        ) : (
                          <div className="max-w-xs">
                            <div className="font-medium text-sm truncate">{request.url}</div>
                            {request.category && (
                              <div className="text-xs text-muted-foreground truncate">
                                Category: {request.category}
                              </div>
                            )}
                          </div>
                        )}
                      </TableCell>
                      <TableCell>
                        <div className="text-sm text-muted-foreground max-w-xs truncate">
                          {request.message || '-'}
                        </div>
                      </TableCell>
                      <TableCell>
                        <Badge variant={getStatusVariant(request.response)} className={getStatusClassName(request.response)}>
                          {getStatusLabel(request.response)}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex justify-end gap-2">
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleAcceptRequest(request, index)}
                            disabled={request.response !== undefined}
                            className="h-8"
                          >
                            <Check className="h-3 w-3 mr-1" />
                            Approve
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleDeclineRequest(request, index)}
                            disabled={request.response !== undefined}
                            className="h-8"
                          >
                            <X className="h-3 w-3 mr-1" />
                            Decline
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Additional info for requests with AI notes */}
      {filteredRequests.some(r => r.aiNotes) && (
        <Card>
          <CardHeader>
            <CardTitle>AI Analysis</CardTitle>
          </CardHeader>
          <CardContent>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>{activeSection === "apps" ? "App Name" : "URL"}</TableHead>
                  <TableHead>AI Notes</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredRequests
                  .filter(r => r.aiNotes)
                  .map((request, index) => (
                    <TableRow key={`ai-${request.device}-${index}`}>
                      <TableCell className="font-medium text-sm max-w-xs truncate">
                        {activeSection === "apps" ? request.name : request.url}
                      </TableCell>
                      <TableCell className="max-w-md">
                        <div className="text-sm text-muted-foreground">
                          {request.aiNotes}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}
        </div>
      </div>
    </div>
  );
};

export const DeviceRequestsTab = ({ deviceId }: DeviceRequestsTabProps) => {
  return (
    <ConvexErrorBoundary>
      <DeviceRequestsContent deviceId={deviceId} />
    </ConvexErrorBoundary>
  );
};
